import JsBarcode from "jsbarcode";
import QRCode from "qrcode";

const escapeHtml = (value) =>
  String(value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

const buildBarcodeSvg = (value) => {
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  JsBarcode(svg, String(value), {
    format: "CODE128",
    height: 56,
    width: 1.6,
    displayValue: true,
    fontSize: 14,
    margin: 8,
    background: "#ffffff",
    lineColor: "#0f172a",
  });
  return svg.outerHTML;
};

/**
 * Print a medicine shelf label with barcode, QR code, or both.
 * @param {{ barcode: string, name?: string, category?: string, price?: string|number, expiry?: string, mode?: 'barcode'|'qr'|'both', copies?: number }} options
 */
export const printMedicineLabel = async ({
  barcode,
  name,
  category,
  price,
  expiry,
  mode = "both",
  copies = 1,
}) => {
  const code = String(barcode || "").trim();
  if (!code) return false;

  const safeName = escapeHtml(name || code);
  const safeBarcode = escapeHtml(code);
  const safeCategory = category ? escapeHtml(category) : "";
  const safePrice = price != null && price !== "" ? escapeHtml(price) : "";
  const safeExpiry = expiry ? escapeHtml(expiry) : "";

  const showBarcode = mode === "barcode" || mode === "both";
  const showQr = mode === "qr" || mode === "both";

  const barcodeSvg = showBarcode ? buildBarcodeSvg(code) : "";
  const qrDataUrl = showQr
    ? await QRCode.toDataURL(code, {
        width: 140,
        margin: 1,
        color: { dark: "#0f172a", light: "#ffffff" },
      })
    : "";

  const metaRows = [
    safeCategory ? `<div class="meta">${safeCategory}</div>` : "",
    safePrice ? `<div class="meta">${safePrice}</div>` : "",
    safeExpiry ? `<div class="meta">${safeExpiry}</div>` : "",
  ]
    .filter(Boolean)
    .join("");

  const labelBody = `
    <div class="label">
      <div class="title">${safeName}</div>
      ${metaRows}
      <div class="codes">
        ${showBarcode ? `<div class="barcode">${barcodeSvg}</div>` : ""}
        ${showQr ? `<img class="qr" src="${qrDataUrl}" alt="QR ${safeBarcode}" />` : ""}
      </div>
      <div class="code-text">${safeBarcode}</div>
    </div>
  `;

  const labels = Array.from({ length: Math.max(1, copies) }, () => labelBody).join(
    '<div class="page-break"></div>',
  );

  const win = window.open("", "medicine-label-print");
  if (!win) return false;

  win.document.write(`
    <html>
      <head>
        <title>${safeName}</title>
        <style>
          @page { size: 80mm 50mm; margin: 4mm; }
          body {
            font-family: Arial, sans-serif;
            margin: 0;
            padding: 8px;
            color: #0f172a;
          }
          .label {
            text-align: center;
            page-break-inside: avoid;
          }
          .title {
            font-size: 14px;
            font-weight: 700;
            margin-bottom: 4px;
            line-height: 1.2;
          }
          .meta {
            font-size: 11px;
            color: #475569;
            margin-bottom: 2px;
          }
          .codes {
            display: flex;
            align-items: center;
            justify-content: center;
            gap: 8px;
            flex-wrap: wrap;
            margin: 6px 0 4px;
          }
          .barcode svg { max-width: 100%; height: auto; }
          .qr { width: 88px; height: 88px; }
          .code-text {
            font-size: 11px;
            letter-spacing: 0.5px;
            margin-top: 2px;
          }
          .page-break { page-break-after: always; height: 0; }
        </style>
      </head>
      <body>${labels}<script>window.onload = () => { window.print(); window.close(); }<\/script></body>
    </html>
  `);
  win.document.close();
  return true;
};

export default printMedicineLabel;
